import React, { Fragment, useMemo, useState } from 'react';
import DownOutlined from '../../atoms/Icon/DownOutlined';

/** Applied to Select so the closed state truncates with ellipsis (Figma). */
export const CHART_FILTER_SELECT_CLASSNAME = 'asper-chart-filter-select w-full min-w-0';

const MAX_VISIBLE_DEFAULT = 4;

function chunkColumns<T>(arr: T[], size: number): T[][] {
    const rows: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        rows.push(arr.slice(i, i + size));
    }
    return rows;
}

/** Expands `<></>` so each column is its own flex item (otherwise one Fragment = one slot → vertical stack). */
function flattenToElements(children: React.ReactNode): React.ReactElement[] {
    const out: React.ReactElement[] = [];
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === Fragment) {
            out.push(...flattenToElements(child.props.children));
        } else {
            out.push(child);
        }
    });
    return out;
}

export type ChartFilterColumnProps = {
    label: React.ReactNode;
    children: React.ReactNode;
};

/** Label above control — matches Ask Asper chart filter spec (#666, 12px). */
export const ChartFilterColumn: React.FC<ChartFilterColumnProps> = ({ label, children }) => (
    <div className="flex flex-col items-stretch min-w-0 w-full pb-1">
        <div className="font-['Open_Sans'] font-normal text-[12px] leading-[22px] text-[#666] truncate min-h-[22px] mb-[-4px]">
            {label}
        </div>
        <div className="min-w-0 mb-[-4px]">{children}</div>
    </div>
);

export type ChartFilterBarProps = {
    children: React.ReactNode;
    /** When there are more than this many filter columns, show “More filters”. Default 4. */
    maxVisible?: number;
    className?: string;
};

/**
 * Collapsed: first `maxVisible` filters in one row (+ More when total > maxVisible).
 * Expanded: every filter shown with at most `maxVisible` filters per row (next filters on the next line).
 */
const ChartFilterBar: React.FC<ChartFilterBarProps> = ({
    children,
    maxVisible = MAX_VISIBLE_DEFAULT,
    className = '',
}) => {
    const [expanded, setExpanded] = useState(false);

    const columns = useMemo(() => flattenToElements(children), [children]);

    const total = columns.length;
    /** Show at most `maxVisible` filters until expanded; toggle only when count exceeds max (e.g. 5+ → first 4 + More). */
    const showToggle = total > maxVisible;

    const visibleColumns = useMemo(() => {
        if (!showToggle || expanded) return columns;
        return columns.slice(0, maxVisible);
    }, [columns, showToggle, expanded, maxVisible]);

    const columnRows = useMemo(() => chunkColumns(visibleColumns, maxVisible), [visibleColumns, maxVisible]);

    const multiRow = columnRows.length > 1;
    const alignOuterWithButton = multiRow ? 'items-start' : 'items-end';

    return (
        <>
            <style>{`
                .asper-chart-filter-select.ant-select .ant-select-selector {
                    overflow: hidden !important;
                }
                .asper-chart-filter-select.ant-select .ant-select-selection-overflow {
                    flex-wrap: nowrap !important;
                    overflow: hidden;
                }
                .asper-chart-filter-select.ant-select .ant-select-selection-overflow-item {
                    max-width: 100%;
                }
                .asper-chart-filter-select.ant-select .ant-select-selection-item,
                .asper-chart-filter-select.ant-select .ant-select-selection-placeholder {
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                }
                .asper-chart-filter-select.ant-select:not(.ant-select-multiple) .ant-select-selection-item {
                    padding-inline-end: 8px;
                }
                /* Plain text in closed multi-select — no pill/chip background */
                .asper-chart-filter-select.ant-select-multiple .ant-select-selection-item {
                    background: transparent !important;
                    border: none !important;
                    margin-inline-end: 0 !important;
                    padding-inline: 0 !important;
                    height: auto !important;
                    line-height: 22px !important;
                }
            `}</style>
            <div
                className={`bg-[#fafafa] rounded-lg px-3 pb-2 pt-1 flex ${alignOuterWithButton} justify-between gap-2 min-w-0 ${className}`}
                data-chart-filter-bar
            >
                <div
                    className={`min-w-0 flex-1 flex flex-col ${multiRow ? 'gap-3' : ''}`}
                >
                    {columnRows.map((row, rowIdx) => (
                        <div
                            key={`chart-filter-row-${rowIdx}`}
                            className="grid gap-2.5 items-end min-w-0 w-full"
                            style={{
                                gridTemplateColumns: `repeat(${maxVisible}, minmax(0, 1fr))`,
                            }}
                        >
                            {row.map((col, i) => (
                                <div
                                    key={
                                        React.isValidElement(col) && col.key != null
                                            ? `${String(col.key)}-${rowIdx}`
                                            : `cf-${rowIdx}-${i}`
                                    }
                                    className="min-w-0"
                                >
                                    {col}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                {showToggle && (
                    <button
                        type="button"
                        className="shrink-0 flex items-center gap-1 py-1 font-['Open_Sans'] font-medium text-[10px] leading-4 tracking-[0.3px] text-[#5f49bc] whitespace-nowrap cursor-pointer bg-transparent border-0 p-0"
                        onClick={() => setExpanded((e) => !e)}
                        data-test-id="chart-filter-more-toggle"
                    >
                        {expanded ? 'Fewer filters' : 'More filters'}
                        <span className={`inline-flex ${expanded ? 'rotate-180' : ''}`}>
                            <DownOutlined size={12} color="#5f49bc" />
                        </span>
                    </button>
                )}
            </div>
        </>
    );
};

export default ChartFilterBar;
